import type { ToolCall } from '@kanbu/schema';
import type { ShowFormArtifact } from '@kanbu/schema/contracts';
import { ToolType } from '@kanbu/schema/enums';
import { Trans } from '@lingui/react/macro';

import { FormArtifactView } from './form-artifact-view';
import { getFormSubmissionData, getFormToolCallState } from './form-session';
import { FormCanceledView, FormSubmittedView } from './form-submitted-view';
import { ToolError, ToolStatus } from '../components';
import type { ToolState } from '../tool-provider';

export interface FormToolCallViewProps {
  artifact: ShowFormArtifact;
  toolCall: ToolCall<Record<string, unknown>>;
  state: ToolState;
  error?: string;
  onSubmit?: (params: {
    toolName: string;
    toolCallId: string;
    data: Record<string, unknown>;
  }) => void | Promise<void>;
}

export function FormToolCallView({
  artifact,
  toolCall,
  state,
  error,
  onSubmit,
}: FormToolCallViewProps) {
  const formState = getFormToolCallState({
    ...toolCall,
    type: ToolType.Tool,
    artifact,
  });

  if (formState === 'canceled') {
    return <FormCanceledView artifact={artifact} />;
  }

  if (formState === 'submitted') {
    const data = getFormSubmissionData({
      ...toolCall,
      type: ToolType.Tool,
      artifact,
    });

    return <FormSubmittedView artifact={artifact} data={data ?? {}} />;
  }

  if (state === 'error') {
    return (
      <ToolError message={<Trans>Could not show form</Trans>} error={error} />
    );
  }

  if (state === 'call' || state === 'call-partial') {
    return (
      <ToolStatus loading>
        <Trans>Preparing form…</Trans>
      </ToolStatus>
    );
  }

  /**
   * The interactive form renders only for an explicitly pending call.
   * Overlay forms are rendered by the overlay host instead of inline.
   */
  if (formState !== 'pending' || artifact.presentation === 'overlay') {
    return null;
  }

  return (
    <FormArtifactView
      artifact={artifact}
      onSubmit={async data => {
        await onSubmit?.({
          toolName: toolCall.name,
          toolCallId: toolCall.id,
          data,
        });
      }}
    />
  );
}
